import React, { useState, useEffect } from "react";
import { useParams, Link, useNavigate, Navigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { getVgsapPromotionDetail, updateVgsapPromotion } from "../../service/SalesNewsVgsap";
import { PageLoader } from "../../component/loader/PLoader";
import ErrorHandler from "../../component/errors/ErrorHandler";
import {
	Box, Typography, Paper, Breadcrumbs, Button, TextField,
	Divider, Snackbar, Alert, IconButton
} from "@mui/material";
import dayjs from "dayjs";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import SaveIcon from "@mui/icons-material/Save";
import AddIcon from "@mui/icons-material/Add";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import useUser from "../../hooks/useUser";
import { EditableDatePicker } from "../../component/datePicker";
import "./salesNewsVgsap.css";
import { formatDateOrNA, DetailField, POINT_SECTIONS_EDIT } from "./vgsapUtils";

// Format date as YYYY-MM-DD for date picker
function formatDateForInput(dateStr) {
	if (!dateStr || dateStr === '1900-01-01' || dateStr.startsWith('1900-01-01')) return '';
	const d = new Date(dateStr);
	if (isNaN(d.getTime())) return '';
	return d.toISOString().split('T')[0];
}

function PromotionEdit() {
	const { promoId } = useParams();
	const { t } = useTranslation();
	const navigate = useNavigate();
	const queryClient = useQueryClient();
	const { isSalesNewsAdmin } = useUser();

	// Form state
	const [promoStart, setPromoStart] = useState('');
	const [promoEnd, setPromoEnd] = useState('');
	const [promoStartError, setPromoStartError] = useState('');
	const [promoEndError, setPromoEndError] = useState('');
	const [description, setDescription] = useState('');
	const [section1, setSection1] = useState(['']);
	const [section2, setSection2] = useState(['']);
	const [section3, setSection3] = useState(['']);
	const [section4, setSection4] = useState(['']);
	const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

	// Fetch promotion detail + points
	const { data: promo, isLoading, error } = useQuery({
		queryKey: ["vgsapPromotionDetail", promoId],
		queryFn: () => getVgsapPromotionDetail(promoId),
	});

	// Populate form when data loads
	useEffect(() => {
		if (promo) {
			setPromoStart(formatDateForInput(promo.start_date));
			setPromoEnd(formatDateForInput(promo.end_date));
			setDescription(promo.description || '');

			// Group points by section
			const points = promo.points || [];
			const getPointsForSection = (bulletId) => {
				const sectionPoints = points
					.filter(p => (p.promo_bullet_id || p.Promo_bullet_id) === bulletId)
					.sort((a, b) => parseInt(a.point_id || a.Point_id || 0) - parseInt(b.point_id || b.Point_id || 0))
					.map(p => p.points || p.Points || '');
				return sectionPoints.length > 0 ? sectionPoints : [''];
			};

			setSection1(getPointsForSection('1'));
			setSection2(getPointsForSection('2'));
			setSection3(getPointsForSection('3'));
			setSection4(getPointsForSection('4'));
		}
	}, [promo]);

	// Save mutation
	const saveMutation = useMutation({
		mutationFn: updateVgsapPromotion,
		onSuccess: () => {
			setSnackbar({ open: true, message: t('Promotion updated successfully'), severity: 'success' });
			// Invalidate related queries so data refreshes
			queryClient.invalidateQueries({ queryKey: ["vgsapPromotionDetail", promoId] });
		},
		onError: (err) => {
			setSnackbar({ open: true, message: err?.message || t('Failed to update promotion'), severity: 'error' });
		},
	});

	const handleSave = () => {
		const payload = {
			promo_id: promoId,
			description,
			start_date: promoStart || '1900-01-01',
			end_date: promoEnd || '1900-01-01',
			point_ind: promo.point_ind || '',
			maint_opid: promo.create_opid || '', // RefLib pattern — uses create_opid as maint_opid
			scd_seq: promo.scd_seq || 0,
			// Points grouped by section
			points_section_1: section1.filter(p => p.trim() !== ''),
			points_section_2: section2.filter(p => p.trim() !== ''),
			points_section_3: section3.filter(p => p.trim() !== ''),
			points_section_4: section4.filter(p => p.trim() !== ''),
		};

		saveMutation.mutate(payload);
	};

	// Point management helpers
	const addPoint = (setter) => setter(prev => [...prev, '']);
	const removePoint = (setter, index) => setter(prev => prev.filter((_, i) => i !== index));
	const updatePoint = (setter, index, value) => setter(prev => prev.map((v, i) => i === index ? value : v));

	// Guard: redirect non-admin users away from edit page
	if (!isSalesNewsAdmin) {
		return <Navigate to="/sales-news/sales-news-vgsap" replace />;
	}

	if (isLoading) return <PageLoader show={true} />;
	if (error) return <ErrorHandler title={t("Something went wrong")} desc={t("Unable to load promotion details")} />;
	if (!promo) return <ErrorHandler title={t("Not Found")} desc={t("Promotion not found")} />;

	return (
		<Box sx={{ p: 3 }} className="vgsap__page">
			{/* Breadcrumb */}
			<Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} sx={{ mb: 2 }}>
				<Link to="/sales-news/sales-news-vgsap" style={{ textDecoration: "none", color: "#1976d2" }}>
					{t("Sales News (Vistex GSAP)")}
				</Link>
				<Link to={`/sales-news/sales-news-vgsap/promotion/${promoId}`} style={{ textDecoration: "none", color: "#1976d2" }}>
					{t("Promotion")} {promoId}
				</Link>
				<Typography color="text.primary">{t("Edit")}</Typography>
			</Breadcrumbs>

			{/* Edit Form */}
			<Paper sx={{ p: 3 }}>
				<Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
					<Typography variant="h5">
						{t("Edit Promotion")} #{promoId}
					</Typography>
					<Button
						variant="contained"
						startIcon={<SaveIcon />}
						onClick={handleSave}
						disabled={saveMutation.isPending}
					>
						{saveMutation.isPending ? t("Saving...") : t("Save")}
					</Button>
				</Box>

				{/* Read-only fields */}
				<Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr 1fr" }, gap: 2.5, mt: 2 }}>
					<DetailField label={t("Promo ID")} value={promo.promo_id} />
					<DetailField label={t("Create Date")} value={formatDateOrNA(promo.create_dt)} />
					<DetailField label={t("Region")} value={promo.grp_id} />
					<DetailField label={t("Bulletin Number")} value={promo.bulletin_no} />
					<DetailField label={t("CPG")} value={promo.scd_code} />
					<DetailField label={t("Created By")} value={promo.create_opid} />
					<DetailField label={t("Last Modified By")} value={promo.maint_opid ? `${promo.maint_opid} on ${formatDateOrNA(promo.maint_dt)}` : 'N/A'} />
				</Box>

				<Divider sx={{ my: 3 }} />

				{/* Editable fields */}
				<Typography variant="h6" gutterBottom>{t("Editable Fields")}</Typography>

				<Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 2.5, mt: 2 }}>
					<Box>
						<Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>
							{t("Promo Start")}
						</Typography>
						<EditableDatePicker
							value={promoStart}
							onChange={(iso) => {
								setPromoStart(iso || "");
								if (iso && promoEnd && dayjs(iso).isAfter(dayjs(promoEnd), "day")) {
									setPromoStartError("Start date cannot be after end date");
								} else {
									setPromoStartError("");
									setPromoEndError("");
								}
							}}
							error={!!promoStartError}
							helperText={promoStartError}
							fullWidth
						/>
					</Box>
					<Box>
						<Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>
							{t("Promo End")}
						</Typography>
						<EditableDatePicker
							value={promoEnd}
							onChange={(iso) => {
								setPromoEnd(iso || "");
								if (iso && promoStart && dayjs(iso).isBefore(dayjs(promoStart), "day")) {
									setPromoEndError("End date cannot be before start date");
								} else {
									setPromoEndError("");
									setPromoStartError("");
								}
							}}
							error={!!promoEndError}
							helperText={promoEndError}
							fullWidth
						/>
					</Box>
				</Box>

				<Box sx={{ mt: 2.5 }}>
					<Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>
						{t("Description")}
					</Typography>
					<TextField
						value={description}
						onChange={(e) => setDescription(e.target.value)}
						multiline
						rows={2}
						fullWidth
						size="small"
					/>
				</Box>

				<Divider sx={{ my: 3 }} />

				{/* Promotion Summary Points — 4 sections */}
				<Typography variant="h6" gutterBottom>{t("Summary Points")}</Typography>

				{POINT_SECTIONS_EDIT.map(({ id, label, stateKey }) => {
					const points = { section1, section2, section3, section4 }[stateKey];
					const setter = { section1: setSection1, section2: setSection2, section3: setSection3, section4: setSection4 }[stateKey];

					return (
						<Box key={id} sx={{ mb: 3 }}>
							<Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
								<Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
									{label}
								</Typography>
								<Button
									size="small"
									variant="outlined"
									startIcon={<AddIcon />}
									onClick={() => addPoint(setter)}
								>
									{t("Add Another")}
								</Button>
							</Box>
							{points.map((point, idx) => (
								<Box key={idx} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
									<TextField
										value={point}
										onChange={(e) => updatePoint(setter, idx, e.target.value)}
										size="small"
										fullWidth
										placeholder={`${label} point ${idx + 1}`}
									/>
									{points.length > 1 && (
										<IconButton
											size="small"
											color="error"
											onClick={() => removePoint(setter, idx)}
											aria-label={t("Remove point")}
										>
											<RemoveCircleOutlineIcon />
										</IconButton>
									)}
								</Box>
							))}
						</Box>
					);
				})}
			</Paper>

			{/* Snackbar for save feedback */}
			<Snackbar
				open={snackbar.open}
				autoHideDuration={5000}
				onClose={() => setSnackbar({ ...snackbar, open: false })}
				anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
			>
				<Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
					{snackbar.message}
				</Alert>
			</Snackbar>
		</Box>
	);
}

export default PromotionEdit;
